import { useEffect, useState } from 'react'
import {
    LuxDialog,
    LuxTextInput,
    LuxRadioInput,
    LuxIconSpinner,
    LuxButton,
} from '@lux/wardrobe-lib'
import { Album } from '@Techedge/digitalportal-shared'
import { connect } from 'react-redux'
import { GalleryService } from '@/app/services/GalleryService'
import { addPhotosToExistingAlbum } from '@/app/redux/actions/save-picture'
import { useIntl } from 'react-intl'

interface AlbumListDialogProps {
    visible: boolean
    close: () => void
    selectedPhotosIds: string[]
    addPhotosToAlbum: (albumId: string, photoIds: string[], successMessage: string) => void
}

const AlbumListDialog = ({
    visible,
    close,
    selectedPhotosIds,
    addPhotosToAlbum,
}: AlbumListDialogProps) => {
    const [albums, setAlbums] = useState<Album[]>([])
    const [totalElements, setTotalElements] = useState(0)
    const [pageSize, setPageSize] = useState(5)
    const [filterText, setFilterText] = useState('')
    const [loading, setLoading] = useState(false)
    const [selectedAlbumId, setSelectedAlbumId] = useState('')

    const intl = useIntl()

    useEffect(() => {
        if (!filterText || filterText.length > 2) {
            setLoading(true)
            GalleryService.GetAlbumsList({ title: filterText, pageSize: pageSize, page: 1 }).then(
                (response) => {
                    setAlbums(response.data?.data)
                    setTotalElements(response.data?.totalRecords)
                    setLoading(false)
                }
            )
        }
    }, [filterText, pageSize])

    const onSelectAlbum = async () => {
        await addPhotosToAlbum(
            selectedAlbumId,
            selectedPhotosIds,
            intl.formatMessage({
                id: 'ph2.gallery.album.added.success_message',
                defaultMessage: 'Photos added to album',
            })
        )
        close()
    }

    return (
        <LuxDialog
            visible={visible}
            onHide={close}
            title={intl.formatMessage({
                id: 'ph2.gallery.detail.add.to.album',
                defaultMessage: 'Add to album',
            })}
            size="md"
            resizable
            onSave={onSelectAlbum}>
            <LuxTextInput
                value={filterText}
                onChangeCallback={setFilterText}
                placeholder={intl.formatMessage({
                    id: 'ph2.gallery.album.enter_keywords',
                    defaultMessage: 'Enter your keywords here',
                })}
                label={intl.formatMessage({
                    id: 'ph2.gallery.album.enter_keywords',
                    defaultMessage: 'Enter your keywords here',
                })}
            />
            {/* Render paginated album list */}
            {loading ? (
                <LuxIconSpinner />
            ) : (
                <>
                    <div className="ml-2 mr-auto">
                        <LuxRadioInput
                            value={selectedAlbumId}
                            onChangeCallback={setSelectedAlbumId}
                            classes="w-100"
                            radioList={albums.map((album) => ({
                                label: album.name,
                                value: album.id,
                            }))}
                        />
                    </div>
                    <div className="w-100 flex justify-content-between mt-3 px-3 align-items-center">
                        <div
                            className="text-mini"
                            style={{ color: 'var(--lux-grey2', fontFamily: 'Inter SemiBold' }}>
                            {totalElements}{' '}
                            {intl.formatMessage({
                                id: 'ph2.gallery.album.total_elements',
                                defaultMessage: 'elements',
                            })}
                        </div>
                        {pageSize < totalElements && (
                            <LuxButton
                                text="View all"
                                variant="text"
                                onClick={() => setPageSize(totalElements)}
                            />
                        )}
                    </div>
                </>
            )}
        </LuxDialog>
    )
}

const mapStateToProps = (state: any) => ({})

const mapDispatchToProps = (dispatch: any) => ({
    addPhotosToAlbum: (albumId: string, photoIds: string[]) =>
        dispatch(addPhotosToExistingAlbum(albumId, photoIds)),
})

export default connect(mapStateToProps, mapDispatchToProps)(AlbumListDialog)
